import {
  type CSSProperties,
  forwardRef,
  type MouseEvent,
  useCallback,
  useMemo
} from 'react';
import styled, { useTheme } from 'styled-components';
import type { PositionValue } from '@elfsight/configurator-contracts';
import { Check } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { Tooltip } from '@elfsight/widget-modal';
import { FloatingButton as LibFloatingButton } from '@elfsight/widget-ui';
import { isMobile } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { defaultSettings } from '../../defaults';
import AccessMan from '../../icons/access-man.svg?react';
import { useModeData } from '../data/use-mode-data';
import { useA11yState } from '../hooks/use-a11y-state';

type FloatingButtonProps = {
  panelOpened: boolean;
  ariaControls: string;
  /**
   * Deactivation should overwrite styles for the floating button
   */
  style?: CSSProperties;
  onClick: (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
};

export const FloatingButton = forwardRef<HTMLDivElement, FloatingButtonProps>(
  ({ panelOpened, ariaControls, style, onClick }, forwardingRef) => {
    const { t } = useLingui();
    const { position, displayDeactivation, renderThumbnailMode } =
      useSettings();
    const theme = useTheme();
    const { a11yState, modifiedSettingsCount } = useA11yState();
    const modeData = useModeData();

    const isMobileDevice = isMobile();
    const { mode } = a11yState;
    const isModeSelected = !!mode;
    const isCustomSettingsApplied = modifiedSettingsCount > 0;
    const disableTooltip = displayDeactivation || isMobileDevice;
    const showButton = renderThumbnailMode || !panelOpened;

    const {
      position: {
        verticalOffset: defaultVerticalOffset,
        horizontalOffset: defaultHorizontalOffset
      }
    } = defaultSettings;

    const modeIcon = useMemo(
      () => (mode ? modeData[mode].icon : null),
      [mode, modeData]
    );

    const AccessIcon = useCallback(
      () => (
        <Icon
          component={modeIcon || AccessMan}
          fill={theme.floatingButton.iconColor}
          size={24}
          aria-hidden="true"
        />
      ),
      [modeIcon, theme.floatingButton.iconColor]
    );

    const showBadge = isModeSelected || isCustomSettingsApplied;

    const badgeSlot = showBadge ? (
      <Badge aria-hidden="true">
        <Icon
          component={Check}
          size={10}
          fill={theme.floatingButton.badgeColor}
          alt={t`Check icon`}
        />
      </Badge>
    ) : undefined;

    const handleClick = useCallback(
      (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
        if (!displayDeactivation) {
          onClick(e);
        }
      },
      [displayDeactivation, onClick]
    );

    return (
      <Tooltip
        disabled={disableTooltip}
        title={
          <TooltipTitle
            selectedModeTitle={isModeSelected ? modeData[mode]?.label : null}
            isCustomSettingsApplied={isCustomSettingsApplied}
          />
        }
      >
        <LibFloatingButton
          ref={forwardingRef}
          aria-expanded={panelOpened}
          aria-controls={ariaControls}
          aria-label={t`Open accessibility settings`}
          tabIndex={0}
          iconOnly
          iconLeft={AccessIcon}
          position={{
            position: `bottom-${position.position}` as PositionValue,
            verticalOffset: renderThumbnailMode
              ? defaultVerticalOffset
              : position.verticalOffset,
            horizontalOffset: renderThumbnailMode
              ? defaultHorizontalOffset
              : position.horizontalOffset
          }}
          font={{ fontSize: 20 }}
          horizontalPadding={8}
          verticalPadding={8}
          color={theme.floatingButton.backgroundColor}
          style={{
            ...style,
            boxShadow: renderThumbnailMode
              ? '0px 6px 30px 0px rgba(0, 0, 0, 0.25)'
              : undefined
          }}
          withShadow={!renderThumbnailMode}
          containerStyle={{
            visibility: showButton ? ('visible' as const) : ('hidden' as const),
            opacity: showButton ? 1 : 0,
            transform: !showButton ? 'translateY(10px)' : undefined,
            transition: showButton
              ? 'opacity 0.2s, visibility 0.3s, transform 0.3s'
              : undefined
          }}
          variants={{
            focusVisible: {
              outline: `3px solid ${theme.floatingButton.outlineColor}`,
              outlineOffset: '2px',
              borderColor: 'none'
            }
          }}
          slot={badgeSlot}
          onClick={handleClick}
        />
      </Tooltip>
    );
  }
);

const Badge = styled.div`
  position: absolute;
  right: -3px;
  bottom: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0ec12f;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.3);
  cursor: pointer;
`;

type TooltipTitleProps = {
  selectedModeTitle: string | null;
  isCustomSettingsApplied: boolean;
};

function TooltipTitle({
  selectedModeTitle,
  isCustomSettingsApplied
}: TooltipTitleProps) {
  const { t } = useLingui();

  const getStatusText = () => {
    if (selectedModeTitle) {
      return t`${selectedModeTitle} mode activated`;
    }

    if (isCustomSettingsApplied) {
      return t`Custom Settings Applied`;
    }

    return null;
  };

  const hasStatus = selectedModeTitle || isCustomSettingsApplied;

  return (
    <TooltipContent>
      <Bold>{t`Accessibility Assistant`}</Bold>
      {hasStatus && (
        <>
          <br />
          {getStatusText()}
        </>
      )}
    </TooltipContent>
  );
}

const TooltipContent = styled.div`
  text-align: left;
  font-size: 15px;
`;

const Bold = styled.span`
  font-weight: 600;
`;
